import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const ProfilePictureContext = createContext();

export const useProfilePicture = () => {
    const context = useContext(ProfilePictureContext);
    if (!context) {
        throw new Error('useProfilePicture debe ser usado dentro de un ProfilePictureProvider');
    }
    return context;
};

export const ProfilePictureProvider = ({ children }) => {
    const [profilePicture, setProfilePicture] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [userNit, setUserNit] = useState(null);
    const [initialized, setInitialized] = useState(false);
    
    // Refs para control fino y estabilidad
    const lastRequestedNit = useRef(null);
    const initializationTimeoutRef = useRef(null);
    const loginTimeoutRef = useRef(null);
    const isLoadingRef = useRef(false);

    // Función mejorada para obtener NIT del usuario
    const getCurrentNit = useCallback(() => {
        try {
            // Intentar obtener NIT de múltiples fuentes en orden de prioridad
            const nitFromCookie = Cookies.get('nit');
            if (nitFromCookie) {
                return nitFromCookie;
            }

            const nitFromSession = sessionStorage.getItem('nit');
            if (nitFromSession) {
                return nitFromSession;
            }

            const nitFromLocal = localStorage.getItem('nit');
            if (nitFromLocal) {
                return nitFromLocal;
            }

            return null;
        } catch (error) {
            console.error('[ProfilePicture] Error obteniendo NIT:', error);
            return null;
        }
    }, []);

    // Función para construir URL completa de imagen
    const buildProxyImageUrl = useCallback((nit) => {
        if (!nit) return null;
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
        const url = `${baseUrl}/images/profile-picture/${nit}`;
        
        // Adjuntar token para permitir carga en etiquetas <img> (que no envían headers)
        const token = Cookies.get('token') || localStorage.getItem('token');
        return token ? `${url}?token=${token}` : url;
    }, []);

    // Función para añadir cache buster
    const addCacheBuster = useCallback((url) => {
        if (!url) return null;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}t=${Date.now()}`;
    }, []);

    // Función principal para cargar imagen de perfil
    const loadProfilePicture = useCallback(async (nit, force = false) => {
        if (!nit) return;

        // Evitar múltiples cargas simultáneas
        if (isLoadingRef.current && !force) {
            console.log('[ProfilePicture] ⏳ Ya hay una carga en progreso, ignorando');
            return;
        }

        // --- OPTIMIZACIÓN SSO ---
        // NO cargar si hay un proceso SSO en marcha, a menos que sea forzado DESPUÉS del SSO
        if (sessionStorage.getItem('sso_in_progress') === 'true' && !force) {
            console.log('[ProfilePicture] 🚦 SSO en progreso, posponiendo carga');
            return;
        }

        // Verificar caché
        if (!force) {
            const hasImage = localStorage.getItem(`profile_picture_${nit}`);
            if (hasImage) {
                // Siempre reconstruir la URL para tener el token fresco
                setProfilePicture(addCacheBuster(buildProxyImageUrl(nit)));
                setUserNit(nit);
                setInitialized(true);
                return;
            }
        }

        try {
            isLoadingRef.current = true;
            setLoading(true);
            
            // Obtener token para evitar 401
            const token = Cookies.get('token') || localStorage.getItem('token');
            const config = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};

            const response = await axios.get(
                `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/images/profile-picture/${nit}`,
                config
            );

            if (response.data.success) {
                const authenticatedUrl = buildProxyImageUrl(nit);
                localStorage.setItem(`profile_picture_${nit}`, 'true');
                setProfilePicture(addCacheBuster(authenticatedUrl));
            } else {
                setProfilePicture(null);
            }
            setUserNit(nit);
            setInitialized(true);
        } catch (error) {
            console.error('[ProfilePicture] Error:', error);
            // Si es 401 o 429 y estamos en SSO, no marcar como error fatal
            if (error.response?.status !== 401) {
                setError(error.message);
            }
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [buildProxyImageUrl, addCacheBuster]);

    // Función para subir nueva imagen
    const uploadProfilePicture = useCallback(async (file, nit) => {
        if (!file || !nit) {
            console.error('[ProfilePicture] File o NIT faltante');
            return false;
        }

        try {
            setLoading(true);
            setError(null);

            const formData = new FormData();
            formData.append('profilePicture', file);

            // Obtener token
            const token = Cookies.get('token');
            if (!token) {
                throw new Error('No hay token de autenticación');
            }

            const response = await axios.post(
                `${import.meta.env.VITE_API_URL}/images/upload-profile-picture`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                console.log('[ProfilePicture] Imagen subida exitosamente');
                // Limpiar caché y recargar
                localStorage.removeItem(`profile_picture_${nit}`);
                localStorage.removeItem(`profile_picture_${nit}_timestamp`);
                await loadProfilePicture(nit, true);
                return true;
            } else {
                throw new Error(response.data.message || 'Error al subir imagen');
            }
        } catch (error) {
            console.error('[ProfilePicture] Error al subir imagen:', error);
            setError(error.message);
            return false;
        } finally {
            setLoading(false);
        }
    }, [loadProfilePicture]);

    // Función para limpiar caché
    const clearCache = useCallback((nit = null) => {
        if (nit) {
            localStorage.removeItem(`profile_picture_${nit}`);
            localStorage.removeItem(`profile_picture_${nit}_timestamp`);
        } else {
            // Limpiar todo el caché de imágenes
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith('profile_picture_')) {
                    localStorage.removeItem(key);
                }
            });
        }
        setProfilePicture(null);
    }, []);

    // Función para forzar refresh
    const forceRefresh = useCallback(() => {
        console.log('[ProfilePicture] Forzando refresh...');
        clearCache();
        if (userNit) {
            loadProfilePicture(userNit, true);
        }
    }, [userNit, loadProfilePicture, clearCache]);

    // Inicialización del contexto
    useEffect(() => {
        // Limpiar cualquier timeout previo si el efecto se relanza
        if (initializationTimeoutRef.current) {
            clearTimeout(initializationTimeoutRef.current);
        }

        const nit = getCurrentNit();
        if (!nit) return;

        // Evitar doble carga si ya se solicitó recientemente para este NIT
        if (lastRequestedNit.current === nit && profilePicture) return;

        // Escalonar la petición inicial
        const jitter = 500; // 500ms fijos para estabilidad durante hidratación
        
        initializationTimeoutRef.current = setTimeout(() => {
            if (nit) {
                console.log(`[ProfilePicture] 🚀 Carga inicial para NIT: ${nit}`);
                lastRequestedNit.current = nit;
                // CORRECCIÓN: No pasar URL como segundo parámetro, solo force (false por defecto)
                loadProfilePicture(nit, false);
            }
            initializationTimeoutRef.current = null;
        }, jitter);

        return () => {
            if (initializationTimeoutRef.current) {
                clearTimeout(initializationTimeoutRef.current);
            }
        };
    }, [getCurrentNit, loadProfilePicture]);

    // Escuchar eventos de login/logout
    useEffect(() => {
        const handleLogin = (event) => {
            const { nit } = event.detail;
            if (nit) {
                // Si es el mismo NIT que ya estamos cargando, ignorar
                if (lastRequestedNit.current === nit && profilePicture) return;

                console.log('[ProfilePicture] 🔑 Login detectado, preparando carga...');
                
                // Limpiar timeout previo si existe
                if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);

                setUserNit(nit);
                setInitialized(false);
                setProfilePicture(null);
                lastRequestedNit.current = nit;
                
                // Aplicar jitter aleatorio
                const jitter = 400; 
                
                loginTimeoutRef.current = setTimeout(() => {
                    loadProfilePicture(nit, true); // Forzar carga tras login
                    loginTimeoutRef.current = null;
                }, jitter);
            }
        };

        const handleLogout = () => {
            console.log('[ProfilePicture] Logout detectado');
            setUserNit(null);
            setProfilePicture(null);
            setInitialized(false);
            setError(null);
            clearCache();
        };

        window.addEventListener('loginSuccess', handleLogin);
        window.addEventListener('userLogout', handleLogout);

        return () => {
            if (loginTimeoutRef.current) clearTimeout(loginTimeoutRef.current);
            window.removeEventListener('loginSuccess', handleLogin);
            window.removeEventListener('userLogout', handleLogout);
        };
    }, [loadProfilePicture, clearCache, profilePicture]);

    // Memoizar el valor del contexto para evitar re-renders innecesarios en consumidores
    const value = React.useMemo(() => ({
        profilePicture,
        loading,
        error,
        userNit,
        hasImage: !!profilePicture,
        initialized,
        uploadProfilePicture,
        loadProfilePicture,
        clearCache,
        forceRefresh,
        debug: () => {
            console.log('[ProfilePicture Debug]', {
                profilePicture,
                userNit,
                hasImage: !!profilePicture,
                loading,
                error,
                initialized
            });
        }
    }), [
        profilePicture, 
        loading, 
        error, 
        userNit, 
        initialized, 
        uploadProfilePicture, 
        loadProfilePicture, 
        clearCache, 
        forceRefresh
    ]);

    return (
        <ProfilePictureContext.Provider value={value}>
            {children}
        </ProfilePictureContext.Provider>
    );
};

export default ProfilePictureContext;